import { decimalAdd, decimalMul } from '@/utils/getCalculatedValue'
import {
  removeNull,
  getTreeArray,
  getSameAttribute
} from '@/utils/handleData.js'

/** 在形象进度树中查找节点 */
function findProgressNode(nodes, uid) {
  const want = String(uid || '')
  if (!want) return null
  for (let i = 0; i < (nodes || []).length; i++) {
    const node = nodes[i]
    if (!node) continue
    const id = String(node.uid || node.value || '')
    if (id === want) return node
    const hit = findProgressNode(node.children, want)
    if (hit) return hit
  }
  return null
}

/** 收集分类节点自身及下级所有分类 uid / code，以及挂接的分项工程 progressUid */
function collectProgressTypeFilter(node) {
  const typeUids = {}
  const typeCodes = {}
  const progressUids = {}
  let codePrefix = ''
  const walk = (current) => {
    if (!current) return
    const id = current.uid || current.value
    if (current.subitemUid) {
      if (id) progressUids[String(id)] = true
      return
    }
    if (id) typeUids[String(id)] = true
    if (current.progressItemCode) {
      const code = String(current.progressItemCode)
      typeCodes[code] = true
      if (!codePrefix) codePrefix = code
    }
    const kids = current.children || []
    kids.forEach(walk)
  }
  walk(node)
  return { typeUids, typeCodes, progressUids, codePrefix }
}

/** 按选中形象分类（含下级分类）筛清单；保留命中复核行及其上级合同清单 */
function filterBillByProgressType(list, typeUid, fullTree) {
  const uid = typeUid != null ? String(typeUid) : ''
  if (!uid) return list || []
  const node = findProgressNode(fullTree, uid)
  if (!node) return list || []
  const { typeUids, typeCodes, progressUids, codePrefix } =
    collectProgressTypeFilter(node)
  const matched = (list || []).filter((item) => {
    if (!item) return false
    if (item.progressUid && progressUids[String(item.progressUid)]) return true
    const typeId =
      item.progressItemTypeUid ||
      item.progressTypeUid ||
      item.progressItemUid
    if (typeId && typeUids[String(typeId)]) return true
    const code = item.progressItemTypeCode
      ? String(item.progressItemTypeCode)
      : ''
    if (code && typeCodes[code]) return true
    if (codePrefix && code && code.indexOf(codePrefix) === 0) return true
    return false
  })
  if (!matched.length) return []
  const byUid = {}
  ;(list || []).forEach((item) => {
    if (item && item.uid) byUid[item.uid] = item
  })
  const keep = {}
  matched.forEach((item) => {
    let cur = item
    while (cur && cur.uid && !keep[cur.uid]) {
      keep[cur.uid] = true
      cur = cur.parentUid ? byUid[cur.parentUid] : null
    }
  })
  return (list || []).filter((item) => item && keep[item.uid])
}

function sumAmount(data, key) {
  return data.reduce((per, cur) => {
    cur[key] = cur[key]
      ? cur[key]
      : cur.children
      ? sumAmount(cur.children, key)
      : 0
    return decimalAdd(per, cur[key])
  }, 0)
}
function sumQuantity(data) {
  if (
    data.children &&
    data.children[0] &&
    data.children[0].listType === '工程量复核清单'
  ) {
    data.billCheckQuantity = getSameAttribute(
      data.children,
      'billCheckQuantity'
    ).addValue
  } else {
    if (data.children && data.children[0]) {
      data.children.forEach((item) => {
        sumQuantity(item)
      })
    }
  }
}
export default {
  namespaced: true,
  state: {
    billOfQuantities: [], // 工程量清单
    billOfReview: []
  },
  getters: {
    billOfContract: (state) => {
      return state.billOfQuantities.filter((item) => {
        return item.listType === '施工合同清单'
      })
    },
    listTree: (state, getters, rootState, rootGetters) => {
      const billOfQuantities = JSON.parse(
        JSON.stringify(state.billOfQuantities)
      )
      const topUid =
        rootGetters['user/orgType'] === 'simPro'
          ? rootState.user.orgValue
          : rootState.bidSection.subBidSection[0]
          ? rootState.bidSection.subBidSection[0].orgUid
          : ''
      var listTree = getTreeArray(billOfQuantities, {
        topUid: topUid
      })
      listTree.forEach((item) => {
        if (!item.children) {
          item.contractAmount = item.contractAmount ? item.contractAmount : 0
          item.billCheckAmount = item.billCheckAmount ? item.billCheckAmount : 0
        } else {
          item.contractAmount = parseFloat(
            sumAmount(item.children, 'contractAmount').toFixed(2)
          )
          item.billCheckAmount = parseFloat(
            sumAmount(item.children, 'billCheckAmount').toFixed(2)
          )
          sumQuantity(item)
        }
      })
      return listTree
    },
    chapterListTree: (state, getters, rootState) => {
      var chapterListTree = JSON.parse(JSON.stringify(getters.listTree))
      if (rootState.radio === 'sum') {
        chapterListTree.forEach((item) => {
          delete item.children
        })
      } else {
        const list = chapterListTree.find((item) => {
          return item.listBreakdownCode === rootState.radio
        })
        chapterListTree = list ? list.children : []
      }
      return chapterListTree
    }
  },
  mutations: {
    setBillOfReview(state, params = {}) {
      state.billOfReview = params.value
    }
  },
  actions: {
    // 工程量清单列表
    async getBillOfQuantities({ rootGetters, commit, dispatch }, data) {
      var queryValues = !data
        ? await dispatch(
            'bidSection/ensureDefaultBidQuery',
            { queryKey: 'billOfQuantities', fieldKey: 'orgUid' },
            { root: true }
          )
        : data
      queryValues = JSON.parse(JSON.stringify(queryValues || {}))
      // 形象分类筛选在前端按树展开；接口仍只认 progressUid（单条分项工程）
      const progressTypeUid =
        queryValues.progressTypeUid || queryValues.progressUid || ''
      delete queryValues.progressTypeUid
      // 选了分类节点时不要带旧的叶子 progressUid，否则会只命中一条
      if (progressTypeUid) {
        delete queryValues.progressUid
      }
      queryValues = removeNull(queryValues)
      if (progressTypeUid && queryValues.orgUid) {
        const tree =
          (rootGetters && rootGetters['progress/boundProgressTypeTree']) || []
        if (!tree.length) {
          await dispatch(
            'progress/getBoundProgressTypeTree',
            { orgUid: queryValues.orgUid },
            { root: true }
          )
        }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/quantitieslistcategory',
          data: queryValues
        },
        { root: true }
      )
      //console.log(res)
      var billOfQuantities = res.data ? res.data : []
      if (progressTypeUid) {
        const fullTree =
          (rootGetters && rootGetters['progress/boundProgressTypeTree']) ||
          []
        billOfQuantities = filterBillByProgressType(
          billOfQuantities,
          progressTypeUid,
          fullTree
        )
      }
      billOfQuantities.forEach((item) => {
        item.listBreakdownCN =
          item.listBreakdownCode + '\xa0\xa0' + item.listBreakdownName
        item.subitemSN = item.subitemUid
          ? item.subitemCode + '\xa0\xa0' + item.subitemName
          : ''
        item.codeName = item.subitemUid ? item.subitemSN : item.listBreakdownCN
        item.contractAmount = item.contractAmount
          ? parseFloat(parseFloat(item.contractAmount).toFixed(2))
          : item.contractQuantity !== null
          ? 0
          : null
        item.billCheckAmount = item.billCheckQuantity
          ? parseFloat(
              decimalMul(item.billCheckQuantity, item.price).toFixed(2)
            )
          : item.billCheckQuantity === 0
          ? 0
          : null
      })
      billOfQuantities.forEach((item) => {
        const subCheckList = billOfQuantities.filter((sub) => {
          return (
            sub.parentUid === item.uid && item.listType === '工程量复核清单'
          )
        })
        if (subCheckList.length) {
          item.billCheckAmount = getSameAttribute(
            subCheckList,
            'billCheckAmount'
          ).addValue
        }
        const parent = billOfQuantities.find((par) => {
          return par.uid === item.parentUid
        })
        item.parentCN = parent
          ? parent.listBreakdownCN
          : rootGetters['user/orgName']
      })
      commit(
        'baseSet',
        {
          module: 'billOfQuantities',
          key: 'billOfQuantities',
          value: billOfQuantities
        },
        { root: true }
      )
    },
    // 新增工程量清单
    async addBillOfQuantities({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/temp/quantitieslistcategory',
          data: data,
          msg: '挂接工程量清单成功'
        },
        { root: true }
      )
      return res
    },
    // 编辑工程量清单
    async editBillOfQuantities({ state, dispatch }, data) {
      var params = data ? data : state.billOfReview[0].children
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/quantitieslistcategory',
          data: params,
          msg: '编辑工程量清单信息成功'
        },
        { root: true }
      )
      return res
    },
    // 删除工程量清单
    async deleteBillOfQuantities({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/quantitieslistcategory',
          data: { uid: uid },
          msg: '删除工程量清单信息成功'
        },
        { root: true }
      )
      return res
    }
  }
}
